import { Signal } from 'typed-signals';
import type { Container } from 'pixi.js';
import type { Popup } from '../../../popups/Popup';
import { RaceEventPopupLayout } from '../../../../layouts/raceEvent/RaceEventPopupLayout';
import { RaceEventPopupJoinView } from './RaceEventPopupJoinView';
import { RaceEventPopupHelpView } from './RaceEventPopupHelpView';
import { RaceEventPopupTrackView } from './RaceEventPopupTrackView';
import type { RaceEventState } from '../types';
import type { PlatformUser } from '../../../app/platform/Platform';
import { getSafeAreaInset } from '../../../safearea/SafeAreaProvider';

export enum RaceEventPage {
  Help = 'Help',
  Join = 'Join',
  Track = 'Track',
}

interface RaceEventPopupConfig {
  initialPage: RaceEventPage;
  player: PlatformUser;
  stateGetter: () => RaceEventState;
  coinTarget: Container;
  boosterTarget: Container;
}

interface PageEntry<T extends Container> {
  helpButton: boolean;
  bigHeader: boolean;
  viewFactory: () => T;
  instance: T | null;
  onFirstShown?: (view: T) => Promise<void>;
}

export class RaceEventPopup implements Popup {
  public static readonly requiredLayouts = [RaceEventPopupLayout];

  public readonly onJoinedClicked = new Signal();

  public readonly onProgressAnimationFinished = new Signal();

  public readonly onEndSequenceFinished = new Signal();

  public readonly onCloseClicked = new Signal();

  private readonly layout = new RaceEventPopupLayout();

  private readonly pagesHistory: RaceEventPage[] = [];

  public readonly root = this.layout.root;

  private currentPage: RaceEventPage | null = null;

  public readonly pages: {
    [RaceEventPage.Join]: PageEntry<RaceEventPopupJoinView>;
    [RaceEventPage.Help]: PageEntry<RaceEventPopupHelpView>;
    [RaceEventPage.Track]: PageEntry<RaceEventPopupTrackView>;
  } = {
    [RaceEventPage.Join]: {
      helpButton: true,
      bigHeader: true,
      viewFactory: () =>
        new RaceEventPopupJoinView(this.onJoinedClicked).props({
          parent: this.layout.topSlot,
        }),
      instance: null,
    },
    [RaceEventPage.Help]: {
      helpButton: false,
      bigHeader: true,
      viewFactory: () =>
        new RaceEventPopupHelpView().props({
          parent: this.layout.centerSlot,
        }),
      instance: null,
    },
    [RaceEventPage.Track]: {
      helpButton: true,
      bigHeader: false,
      viewFactory: () =>
        new RaceEventPopupTrackView({
          player: this.config.player,
          state: this.config.stateGetter(),
          endSequenceSlot: this.layout.endSequenceSlot,
          coinTarget: this.config.coinTarget,
          boosterTarget: this.config.boosterTarget,
          setupForRaceStartAnimation: this.config.initialPage === RaceEventPage.Join,
        }).props({
          parent: this.layout.bottomSlot,
        }),
      instance: null,
      onFirstShown: async (view: RaceEventPopupTrackView) => {
        if (this.config.initialPage === RaceEventPage.Join) {
          await view.wait(200);
          await view.animateRaceStart();
        } else {
          await view.wait(500);
        }

        await view.animateUpdate();
        this.onProgressAnimationFinished.emit();

        const state = this.config.stateGetter();
        if (state.finished) {
          await this.startEndSequence();
        }
      },
    },
  };

  constructor(private readonly config: RaceEventPopupConfig) {
    const { helpButton, closeButton } = this.layout;

    helpButton.on('pointerdown', () => {
      void this.showPage(RaceEventPage.Help);
    });

    closeButton.on('pointerdown', () => {
      if (this.currentPage === RaceEventPage.Help) {
        void this.showPage(this.pagesHistory[this.pagesHistory.length - 2]);
        return;
      }
      this.onCloseClicked.emit();
    });
  }

  public onShown(): void {
    this.root.tweenFrom({ alpha: 0 }, 200, 'sinOut', true);
    void this.showPage(this.config.initialPage, true);
  }

  public async showPage(page: RaceEventPage, skipAnimation = false): Promise<void> {
    void this.hideCurrentPage();

    this.pagesHistory.push(page);

    const entry = this.pages[page];

    const firstShown = entry.instance === null;

    this.layout.header.interactiveChildren = false;
    const instance = entry.instance ?? entry.viewFactory();
    entry.instance = instance;
    this.currentPage = page;

    instance.interactiveChildren = false;

    this.updateHeader(skipAnimation);

    if (!skipAnimation) {
      instance.props({ alpha: 0 });
      await instance.tweenTo({ alpha: 1 }, 200, 'sinOut', true).asyncOnComplete();
    }

    if (firstShown) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      await entry?.onFirstShown?.(instance);
    }

    instance.interactiveChildren = true;
    this.layout.header.interactiveChildren = true;
  }

  private updateHeader(skipAnimation: boolean): void {
    if (!this.currentPage) return;
    const { helpButton, titleWrapper } = this.layout;
    const { bigHeader, helpButton: helpButtonActive } = this.pages[this.currentPage];

    const headerProps = bigHeader ? titleWrapper.data.big : titleWrapper.data.small;
    const helpButtonAlpha = helpButtonActive ? 1 : 0;

    helpButton.eventMode = helpButtonActive ? 'static' : 'none';

    if (skipAnimation) {
      titleWrapper.props(headerProps);
      helpButton.alpha = helpButtonAlpha;
    } else {
      titleWrapper.tweenTo(headerProps, 200, 'sinOut', true);
      helpButton.tweenTo({ alpha: helpButtonAlpha }, 200, 'sinOut', true);
    }
  }

  private async hideCurrentPage(): Promise<void> {
    if (!this.currentPage) return;
    const { instance } = this.pages[this.currentPage];
    if (!instance) return;
    instance.interactiveChildren = false;
    await instance.tweenTo({ alpha: 0 }, 200, 'sinIn', true).asyncOnComplete();
  }

  public async startEndSequence(): Promise<void> {
    this.layout.header.interactiveChildren = false;
    const trackView = this.pages[RaceEventPage.Track].instance;
    if (!trackView) return;

    trackView.interactiveChildren = false;

    trackView.onHideRequested.connect(() => {
      this.layout.wrapper.tweenTo({ alpha: 0 }, 200, 'sinOut', true);
    });
    trackView.onHideOverlayRequested.connect(() => {
      this.layout.darkOverlay.tweenTo({ alpha: 0 }, 200, 'sinOut', true);
    });
    await trackView.startEndSequence();
    this.onEndSequenceFinished.emit();
  }

  public resize(): void {
    const { backgroundGradient, bottomSlot, centerSlot, darkOverlay, header } = this.layout;
    const { width, height } = this.root.pixi.renderer;
    const safeArea = getSafeAreaInset();
    
    backgroundGradient.props({
      width: width * 1.1,
      height: height * 1.1,
    });

    darkOverlay.clear();
    darkOverlay.rect(width * -0.5, 0, width, height);
    darkOverlay.fill({ color: 0x000000, alpha: 0.5 });

    this.root.y = height * -0.5;

    // Apply safe area to entire header container
    header.y = safeArea.top;

    // Apply safe area to slots
    bottomSlot.props({
      y: height - safeArea.bottom,
    });

    centerSlot.props({
      y: height * 0.5,
      x: safeArea.left,
      width: width - safeArea.left - safeArea.right,
    });
  }
}
